export type FireType = 'lean' | 'regular' | 'fat';

export interface FireFormValues {
  currentAge: number;
  monthlyExpense: number;
  currentAssets: number;
  monthlyInvestment: number;
  expectedReturn: number;
  withdrawalRate: number;
  fireType: FireType;
}

export interface FireYearlyDataPoint {
  age: number;
  totalAssets: number;
  fireNumber: number;
  progressPct: number;
}

export interface FireResult {
  fireNumber: number;
  annualExpense: number;
  yearsToFire: number;
  fireAge: number;
  monthlyRequired: number;
  yearlyData: FireYearlyDataPoint[];
}

const MANWON_TO_WON = 10_000;

export function getFireType(monthlyExpense: number): FireType {
  if (monthlyExpense < 200) {
    return 'lean';
  }

  if (monthlyExpense <= 500) {
    return 'regular';
  }

  return 'fat';
}

export function calculateFire(values: FireFormValues): FireResult {
  const annualExpense = values.monthlyExpense * MANWON_TO_WON * 12;
  const fireNumber = annualExpense / (values.withdrawalRate / 100);
  const annualRate = values.expectedReturn / 100;
  const yearlyContribution = values.monthlyInvestment * MANWON_TO_WON * 12;
  const yearlyData: FireYearlyDataPoint[] = [];

  let totalAssets = values.currentAssets * MANWON_TO_WON;
  let yearsToFire = totalAssets >= fireNumber ? 0 : -1;
  let fireAge = totalAssets >= fireNumber ? values.currentAge : -1;

  yearlyData.push({
    age: values.currentAge,
    totalAssets,
    fireNumber,
    progressPct: Math.min(999, (totalAssets / fireNumber) * 100),
  });

  for (let year = 1; year <= 60; year += 1) {
    totalAssets = totalAssets * (1 + annualRate) + yearlyContribution;
    const age = values.currentAge + year;
    const progressPct = Math.min(999, (totalAssets / fireNumber) * 100);

    yearlyData.push({
      age,
      totalAssets,
      fireNumber,
      progressPct,
    });

    if (yearsToFire < 0 && totalAssets >= fireNumber) {
      yearsToFire = year;
      fireAge = age;
    }
  }

  const targetYears = yearsToFire > 0 ? yearsToFire : 20;

  return {
    fireNumber,
    annualExpense,
    yearsToFire,
    fireAge,
    monthlyRequired: calculateMonthlyRequired(fireNumber, values.currentAssets * MANWON_TO_WON, targetYears, values.expectedReturn),
    yearlyData,
  };
}

function calculateMonthlyRequired(targetAmount: number, currentAssets: number, years: number, expectedReturn: number): number {
  const months = Math.max(1, years * 12);
  const monthlyRate = expectedReturn / 100 / 12;

  if (monthlyRate === 0) {
    return Math.max(0, (targetAmount - currentAssets) / months);
  }

  const futureValueOfCurrentAssets = currentAssets * (1 + monthlyRate) ** months;
  const annuityFactor = ((1 + monthlyRate) ** months - 1) / monthlyRate;

  return Math.max(0, (targetAmount - futureValueOfCurrentAssets) / annuityFactor);
}

export function getFireCallout(result: FireResult) {
  if (result.yearsToFire < 0) {
    return {
      title: '현재 투자로는 FIRE 달성이 어렵습니다',
      body: '월 투자액을 늘리거나 목표 생활비를 줄이면 달성 가능성이 크게 올라갑니다. 우선 목표 자산부터 다시 설계해 보세요.',
      cta: { label: '목표 설계 계산기로 다시 계산하기', href: '/retirement/goal' },
    };
  }

  if (result.yearsToFire <= 15) {
    return {
      title: 'FIRE 달성이 현실적인 구간입니다',
      body: '수익률을 조금만 높여도 도달 시점이 빨라질 수 있습니다. ISA·IRP 같은 절세 계좌를 함께 쓰면 복리 효율이 좋아집니다.',
      cta: { label: 'ISA·IRP 세제혜택 계산기 보기', href: '/tax-benefit' },
    };
  }

  return {
    title: '배당 ETF로 FIRE 이후 현금 흐름도 함께 설계해 보세요',
    body: '달성까지 시간이 남아 있다면 목표 자산 규모뿐 아니라 은퇴 후 월 현금 흐름까지 같이 설계하는 편이 안정적입니다.',
    cta: { label: '배당 ETF 계산기로 현금 흐름 보기', href: '/dividend' },
  };
}
